import React, { useState } from 'react';
import { Card, Form, Button, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FaLock, FaEye, FaEyeSlash, FaKey } from 'react-icons/fa';
import { useSettings } from '../../../context/SettingsContext';

const AdminPasswordSettings = () => {
  const { isUpdatingPassword, updatePassword } = useSettings();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setSuccessMessage('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    updatePassword(formData)
      .then(result => {
        if (result.success) {
          setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          setSuccessMessage('Password changed successfully!');
        } else {
          setErrors({ currentPassword: result.message });
        }
      });
  };

  return (
    <Card className="adm-settings-card">
      <Card.Header className="adm-settings-card-header">
        <h3><FaLock className="me-2" />Change Password</h3>
        <p className="adm-settings-card-subtitle">Ensure your account is secure</p>
      </Card.Header>

      <Card.Body>
        {successMessage && (
          <Alert variant="success" className="adm-settings-alert">
            {successMessage}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>
              <FaKey className="me-2" />Current Password *
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswords.current ? 'text' : 'password'}
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
                isInvalid={!!errors.currentPassword}
                className="adm-settings-input"
              />
              <Button
                variant="outline-secondary"
                onClick={() => togglePasswordVisibility('current')}
                className="adm-settings-password-toggle"
              >
                {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.currentPassword}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <FaLock className="me-2" />New Password *
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
                isInvalid={!!errors.newPassword}
                className="adm-settings-input"
              />
              <Button
                variant="outline-secondary"
                onClick={() => togglePasswordVisibility('new')}
                className="adm-settings-password-toggle"
              >
                {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.newPassword}
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              Password must be at least 8 characters with uppercase, lowercase, and numbers
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>
              <FaLock className="me-2" />Confirm New Password *
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                isInvalid={!!errors.confirmPassword}
                className="adm-settings-input"
              />
              <Button
                variant="outline-secondary"
                onClick={() => togglePasswordVisibility('confirm')}
                className="adm-settings-password-toggle"
              >
                {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
              </Button>
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          <Alert variant="info" className="adm-settings-password-info">
            <strong>Password Requirements:</strong>
            <ul className="mb-0 mt-2">
              <li>At least 8 characters long</li>
              <li>Contains uppercase letters (A-Z)</li>
              <li>Contains lowercase letters (a-z)</li>
              <li>Contains numbers (0-9)</li>
              <li>Different from current password</li>
            </ul>
          </Alert>

=          <div className="adm-settings-form-footer">
            <Button
              variant="primary"
              type="submit"
              disabled={isUpdatingPassword}
              className="adm-settings-btn-primary"
            >
              {isUpdatingPassword ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Updating Password...
                </>
              ) : (
                <>
                  <FaKey className="me-2" />
                  Change Password
                </>
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AdminPasswordSettings;